import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { BsGrid3X3GapFill, BsFillEnvelopeFill } from "react-icons/bs";
import BellIconWithNotifications, {
  NotificationWidget,
} from "../notifications/notificationwidget";
import { IoMap } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";

const RenewalExpiration = () => {
  const [expirationDay, setExpirationDay] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL;

  // Function to fetch current expiration day value
  const fetchCurrentValue = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("Authentication token not found");
        return;
      }

      const response = await fetch(
        `${apiUrl}fetch-renewals-expiration-day-count`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExpirationDay(data.data?.value || "");
      } else {
        const errorData = await response.json();
        setError(
          errorData.message || "Failed to fetch current expiration day value"
        );
      }
    } catch (error) {
      console.error("Error fetching current value:", error);
      setError("Failed to fetch current expiration day value");
    } finally {
      setLoading(false);
    }
  };

  // Function to update expiration day
  const updateExpirationDay = async (value) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("accessToken");

      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(
        `${apiUrl}update-renewals-expiration-day-count`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            value: value,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      setSuccess(`Expiration day updated successfully to ${value} days`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (error) {
      console.error("Error updating expiration day:", error);
      setError(error.message || "Failed to update expiration day");

      // Clear error message after 5 seconds
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  // Load current value on component mount
  useEffect(() => {
    fetchCurrentValue();
  }, []);

  const handleChange = (e) => {
    setExpirationDay(e.target.value);
    setError(""); // Clear any existing errors
    setSuccess(""); // Clear any existing success messages
  };

  const handleSave = async () => {
    if (!expirationDay || expirationDay <= 0) {
      setError("Please enter a valid number of days (greater than 0).");
      return;
    }

    await updateExpirationDay(expirationDay);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  return (
    <>
      <div className="dashboard-title mb-3">
        <h1>Renewal Expiration</h1>
        <div className="d-flex align-items-center gap-3">
          <BellIconWithNotifications />
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="rounded-circle border-0 bg-transparent"
              >
                <div
                  className="menu-icon"
                  style={{
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsGrid3X3GapFill />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item
                  href="/dashboard"
                  className="dropdown-item-custom"
                >
                  <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                  Email
                </Dropdown.Item>
                <Dropdown.Item
                  href="/geographical-concentration"
                  className="dropdown-item-custom"
                >
                  <IoMap className="dropdown-icon" /> Geographical Concentration
                </Dropdown.Item>
                <Dropdown.Item
                  href="/renewals-dashboard"
                  className="dropdown-item-custom"
                >
                  <FiRefreshCw className="dropdown-icon" /> Renewals
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="content-body mb-3">
        {/* Error Alert */}
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show mb-3"
            role="alert"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div
            className="alert alert-success alert-dismissible fade show mb-3"
            role="alert"
          >
            {success}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccess("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="row align-items-center">
          <label className="col-sm-3 col-form-label fw-semibold">
            Set Expiration Day
          </label>
          <div className="col-sm-5">
            <input
              type="number"
              className="form-control"
              placeholder="Enter days"
              value={expirationDay}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              disabled={loading}
              min="1"
              max="365"
            />
            <small className="form-text text-muted">
              Enter number of days before renewal expiration (1-365)
            </small>
          </div>
          <div className="col-sm-2">
            <button
              className="btn btn-primary w-100"
              onClick={handleSave}
              disabled={loading || !expirationDay}
              style={{ backgroundColor: "#122066", border: "none" }}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>

        {/* Loading indicator for initial fetch */}
        {loading && !expirationDay && (
          <div className="text-center mt-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RenewalExpiration;
